import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import { GatewayStatusPill } from "../../../components/GatewayStatus";
import { PLC_EDITOR_URL } from "../constants";
import {
  confirmPLCFleetRollout,
  confirmPLCDeploymentSigningPolicy,
  controlPLCFleetRollout,
  getPLCDeploymentSigningPolicy,
  getPLCFleetRollout,
  listEdgeGateways,
  listPLCFleetRolloutEvents,
  listPLCFleetRollouts,
  listPLCProgramsForFleet,
  previewPLCFleetRollout,
  previewPLCDeploymentSigningPolicy,
  rejectPLCFleetRolloutDraft,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import chevronRightIcon from "../../../assets/chevron-right.svg";
import refreshIcon from "../../../assets/refresh.svg";
import xmarkIcon from "../../../assets/xmark.svg";

const phaseLabels = {
  active: "In progress",
  cancelled: "Cancelled",
  cancelling: "Cancelling",
  failed: "Failed",
  health_wait: "Checking health",
  healthy: "Healthy",
  paused: "Paused",
  pending: "Waiting",
  queued: "Queued",
  rejected: "Rejected",
  rollback_failed: "Rollback failed",
  rolled_back: "Rolled back",
  rolling_back: "Rolling back",
  succeeded: "Completed",
  timed_out: "Timed out",
  unreachable: "Offline",
  unknown: "Unknown",
};

export const fleetRolloutPhaseLabel = (value) => {
  const normalized = String(value || "unknown").toLowerCase();
  if (phaseLabels[normalized]) return phaseLabels[normalized];
  const words = normalized.replaceAll("_", " ");
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

export const fleetRolloutEventLabel = (value) => ({
  command_queued: "Runtime update queued",
  draft_created: "Review prepared",
  draft_rejected: "Deployment rejected",
  rollout_cancel: "Deployment cancelled",
  rollout_confirmed: "Deployment approved",
  rollout_pause: "Deployment paused",
  rollout_resume: "Deployment resumed",
  rollout_rollback: "Rollback started",
  rollout_tick: "Status updated",
  runtime_health: "Runtime health checked",
  runtime_receipt: "Runtime responded",
}[value] || fleetRolloutPhaseLabel(value));

const runPolicyLabel = (value) => ({
  preserve: "Keep each runtime's current state",
  start: "Start the application",
  stop: "Keep the application stopped",
}[value] || "Keep each runtime's current state");

const executionModeLabel = (value) => value === "compatibility" ? "Compatibility" : "Compiled";
const rolloutActionResult = (value) => ({
  cancel: "cancelling",
  pause: "paused",
  resume: "in progress",
  rollback: "rolling back",
}[value] || "updated");
const terminalPhases = new Set(["succeeded", "failed", "cancelled", "rolled_back"]);

const runtimePayload = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try { return JSON.parse(value); } catch { return {}; }
  }
  return value;
};

const applicationState = (state, offline = false) => {
  const normalized = String(state || "unknown").toLowerCase();
  if (offline) return { label: "Offline", tone: "isWarning", offline: true };
  if (normalized === "running") return { label: "Running", tone: "isPositive" };
  if (["faulted", "fault", "binding_error"].includes(normalized)) {
    return { label: normalized === "binding_error" ? "Binding error" : "Faulted", tone: "isDanger" };
  }
  if (normalized === "stopped") return { label: "Stopped", tone: "isNeutral" };
  return { label: "State unknown", tone: "isNeutral" };
};

export const buildPLCApplicationRows = (programs, gateways) => (Array.isArray(programs) ? programs : []).map((program) => {
  const publishedVersion = Number(program?.published_version || 0);
  const targets = [];
  if (publishedVersion > 0) {
    const local = applicationState(program?.runtime?.state);
    targets.push({ id: "local", name: "This gateway", ...local });
  }
  (Array.isArray(gateways) ? gateways : []).forEach((gateway) => {
    const snapshot = runtimePayload(gateway?.runtime);
    const deployed = (Array.isArray(snapshot?.plc_programs) ? snapshot.plc_programs : []).find((candidate) => (
      String(candidate?.source_program_id || "") === String(program?.id || "") ||
      (!candidate?.source_program_id && String(candidate?.name || "") === String(program?.name || ""))
    ));
    if (!deployed) return;
    const state = applicationState(deployed?.runtime?.state, gateway.status !== "online");
    targets.push({ id: gateway.id, name: gateway.name || "Edge runtime", ...state });
  });
  const faulted = targets.some((target) => target.tone === "isDanger");
  const running = targets.some((target) => target.label === "Running");
  const offlineCount = targets.filter((target) => target.offline).length;
  const stopped = targets.length > 0 && targets.every((target) => target.label === "Stopped");
  const summary = publishedVersion === 0
    ? { label: "Draft only", tone: "isNeutral" }
    : faulted ? { label: "Needs attention", tone: "isDanger" }
      : offlineCount > 0
        ? { label: `${offlineCount} runtime${offlineCount === 1 ? "" : "s"} offline`, tone: "isWarning" }
        : running ? { label: "Running", tone: "isPositive" }
          : stopped ? { label: "Stopped", tone: "isNeutral" }
            : { label: "Published", tone: "isNeutral" };
  return { ...program, publishedVersion, targets, summary };
});

export const deploymentSigningOpenFromSearch = (search) => (
  new URLSearchParams(search || "").get("setup") === "signing"
);

export const signingPolicyAction = (status, enrolledRuntimeCount = 0) => {
  if (!status) return { ready: false, mutable: false, label: "Unavailable", blocked: true };
  if (status.mode === "environment") {
    return { ready: Boolean(status.active), mutable: false, label: "Managed automatically", blocked: true };
  }
  if (!status.mutable) return { ready: Boolean(status.active), mutable: false, label: "Read only", blocked: true };
  if (enrolledRuntimeCount > 0) {
    return {
      ready: Boolean(status.active), mutable: true,
      label: status.active ? "Changes unavailable" : "Setup unavailable",
      blocked: true,
    };
  }
  return {
    ready: Boolean(status.active), mutable: true,
    label: status.active ? "Review security change" : "Set up deployment security",
    blocked: false,
  };
};

const signingPolicyTone = (status) => status?.active ? "isPositive" : "isWarning";
const signingTTLLabel = (seconds) => {
  const value = Number(seconds || 0);
  if (value % 86400 === 0) return `${value / 86400} day${value === 86400 ? "" : "s"}`;
  if (value % 3600 === 0) return `${value / 3600} hour${value === 3600 ? "" : "s"}`;
  return `${value} seconds`;
};

export const fleetRolloutPhaseTone = (phase) => {
  if (["succeeded", "healthy", "rolled_back"].includes(phase)) return "isPositive";
  if (["failed", "rejected", "timed_out", "rollback_failed", "unreachable"].includes(phase)) return "isDanger";
  if (["paused", "cancelling", "rolling_back", "health_wait"].includes(phase)) return "isWarning";
  return "isNeutral";
};

export const fleetRolloutControlsAvailable = ({ phase, isDraft, canOperate }) =>
  Boolean(canOperate) && !isDraft && phase !== "cancelling" && !terminalPhases.has(phase);

// A draft is a paused rollout that has never been confirmed. The server does
// not serialize that fact directly: rollout.Record.ConfirmedBy is `json:"-"`
// (edge/rollout/record.go), and `approval_required` is unrelated policy
// metadata (whether a *different* engineer must confirm — see
// edgerelayserver/server_fleet_rollout.go: `"approval_required":
// s.fleetRequireApprover`), not a per-record draft flag. So this checks two
// signals instead of one prose string: the exact reason the server sets on
// draft creation (machine.Pause("awaiting explicit confirmation", ...) in
// server_fleet_rollout.go), OR — structurally, independent of any wording —
// every target still sitting at its initial "pending" phase, which is only
// true for a rollout that has never left its paused draft state.
const DRAFT_PAUSE_REASON = "awaiting explicit confirmation";
export const isUntouchedDraftState = (state) => {
  if (state?.phase !== "paused") return false;
  if (state?.reason === DRAFT_PAUSE_REASON) return true;
  const targets = Array.isArray(state?.targets) ? state.targets : [];
  return targets.length > 0 && targets.every((target) => target?.phase === "pending");
};

function RolloutModal({ rollout, approvalRequired, proposedControl, canOperate, events, onClose, onRefresh, onConfirmed }) {
  const notify = useNotify();
  const [confirmReason, setConfirmReason] = useState("");
  const [busy, setBusy] = useState("");
  const [action, setAction] = useState(proposedControl?.action || null);
  const [actionReason, setActionReason] = useState(proposedControl?.reason || "");
  const [rollbackActivated, setRollbackActivated] = useState(proposedControl?.rollback_activated !== false);
  const plan = rollout?.plan;
  const state = rollout?.state;
  const isDraft = isUntouchedDraftState(state);

  const confirm = async () => {
    if (confirmReason.trim().length < 3) return;
    setBusy("confirm");
    try {
      const updated = await confirmPLCFleetRollout(
        rollout.id,
        plan.plan_sha256,
        confirmReason.trim()
      );
      notify({ type: "success", title: "Deployment approved", message: "The deployment can now begin on the selected runtimes." });
      onConfirmed(updated);
    } catch (_err) {
      notify({ type: "error", title: "Approval failed", message: "Could not approve this deployment. Refresh and try again." });
    } finally {
      setBusy("");
    }
  };

  const rejectDraft = async () => {
    if (confirmReason.trim().length < 3) return;
    setBusy("reject");
    try {
      await rejectPLCFleetRolloutDraft(rollout.id, plan.plan_sha256, confirmReason.trim());
      notify({ type: "success", title: "Deployment rejected", message: "No runtimes were changed." });
      await onRefresh();
    } catch (_err) {
      notify({ type: "error", title: "Rejection failed", message: "Could not reject this deployment. Refresh and try again." });
    } finally {
      setBusy("");
    }
  };

  const runAction = async () => {
    setBusy(action);
    try {
      const payload = action === "resume" ? {} : {
        reason: actionReason.trim(),
        rollback_activated: action === "cancel" && rollbackActivated,
      };
      const updated = await controlPLCFleetRollout(rollout.id, action, payload);
      notify({ type: "success", title: "Deployment updated", message: `The deployment is now ${rolloutActionResult(action)}.` });
      setAction(null);
      setActionReason("");
      onConfirmed(updated);
    } catch (_err) {
      notify({ type: "error", title: "Deployment update failed", message: "Could not update this deployment. Refresh and try again." });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose} style={{ zIndex: 115 }}>
      <div
        className="gwModal gwFleetModal"
        data-gw-ai-form-control="reviewed-plc-deployment"
        data-gw-ai-protected-submit="reviewed-plc-fleet-control"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{plan?.spec?.name || "PLC deployment"}</div>
            <div className="gwModalSubtitle">Review this multi-runtime deployment before it begins.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>

        <div className="gwModalBody">
          <div className="gwFleetSummary">
            <GatewayStatusPill tone={fleetRolloutPhaseTone(state?.phase)}>{fleetRolloutPhaseLabel(state?.phase)}</GatewayStatusPill>
            <span>{state?.targets?.length || 0} runtimes</span>
            <span>Expires {plan?.spec?.expires_at ? new Date(plan.spec.expires_at).toLocaleString() : "unknown"}</span>
          </div>
          <div className="gwFleetDigestBox">
            <strong>Deployment settings</strong>
            <div>Engine: {executionModeLabel(plan?.spec?.desired?.execution_mode)}</div>
            <div>After deployment: {runPolicyLabel(plan?.spec?.run_policy)}</div>
            <div>Health check: {plan?.spec?.health?.stable_seconds || 30} seconds of stable operation</div>
          </div>

          <div className="gwFleetTargets">
            {(state?.targets || []).map((target) => {
              const planned = plan?.spec?.targets?.find((item) => item.install_id === target.install_id);
              return (
                <div className="gwFleetTarget" key={target.install_id}>
                  <div>
                    <strong>{planned?.runtime_name || "Edge runtime"}</strong>
                    <div className="gwHint">{fleetRolloutPhaseLabel(target.stage || "fleet")}</div>
                  </div>
                  <div className="gwFleetTargetState">
                    <GatewayStatusPill tone={fleetRolloutPhaseTone(target.phase)}>{fleetRolloutPhaseLabel(target.phase)}</GatewayStatusPill>
                  </div>
                </div>
              );
            })}
          </div>

          {isDraft && (
            <div className="gwFleetConfirmBox">
              <div className="gwSectionTitle">Approve deployment</div>
              <div className="gwHint">
                {approvalRequired
                  ? "A different engineer must approve this deployment before any runtime changes."
                  : "Approval starts this reviewed deployment on the selected runtimes."}
              </div>
              <textarea
                className="gwTextarea"
                value={confirmReason}
                onChange={(event) => setConfirmReason(event.target.value)}
                placeholder="Why is this rollout approved?"
                disabled={!canOperate || busy === "confirm"}
              />
              <div className="gwHeaderActions">
                <button type="button" className="gwGhostBtn" disabled={!canOperate || confirmReason.trim().length < 3 || Boolean(busy)} onClick={rejectDraft}>
                  {busy === "reject" ? "Rejecting…" : "Reject"}
                </button>
                <button
                  type="button"
                  className="gwPrimaryBtn"
                  disabled={!canOperate || confirmReason.trim().length < 3 || Boolean(busy)}
                  onClick={confirm}
                >
                  {busy === "confirm" ? "Approving…" : "Approve deployment"}
                </button>
              </div>
            </div>
          )}

          {state?.phase === "cancelling" && (
            <div className="gwInlineNotice isWarning">
              Cancellation is finishing work already sent to the runtimes. Their status will update automatically.
            </div>
          )}

          {fleetRolloutControlsAvailable({ phase: state?.phase, isDraft, canOperate }) && (
            <div className="gwFleetControls">
              {state?.phase === "active" && <button type="button" className="gwGhostBtn" onClick={() => setAction("pause")}>Pause</button>}
              {state?.phase === "paused" && <button type="button" className="gwGhostBtn" onClick={() => setAction("resume")}>Resume</button>}
              <button type="button" className="gwGhostBtn" onClick={() => setAction("rollback")}>Roll back updated</button>
              <button type="button" className="gwDangerBtn" onClick={() => setAction("cancel")}>Cancel</button>
            </div>
          )}

          {action && (
            <div className="gwFleetActionBox">
              <strong>{action === "rollback" ? "Roll back updated runtimes" : `${fleetRolloutPhaseLabel(action)} deployment`}</strong>
              {action !== "resume" && (
                <textarea
                  className="gwTextarea"
                  value={actionReason}
                  onChange={(event) => setActionReason(event.target.value)}
                  placeholder="Operator reason"
                />
              )}
              {action === "cancel" && (
                <label className="gwFleetCheck">
                  <input type="checkbox" checked={rollbackActivated} onChange={(event) => setRollbackActivated(event.target.checked)} />
                  Roll back runtimes already updated
                </label>
              )}
              <div className="gwHeaderActions">
                <button type="button" className="gwGhostBtn" onClick={() => setAction(null)}>Keep current state</button>
                <button
                  type="button"
                  className={action === "cancel" ? "gwDangerBtn" : "gwPrimaryBtn"}
                  disabled={busy === action || (action !== "resume" && actionReason.trim().length < 3)}
                  onClick={runAction}
                >
                  {busy === action ? "Applying…" : fleetRolloutPhaseLabel(action)}
                </button>
              </div>
            </div>
          )}

          <div className="gwFleetAudit">
            <div className="gwSectionTitleRow gwSectionTitle">
              <span>Audit timeline</span>
              <button type="button" className="gwIconButton" onClick={onRefresh} aria-label="Refresh audit timeline" title="Refresh">
                <img src={refreshIcon} alt="" className="gwIcon" />
              </button>
            </div>
            {(events || []).length === 0 && <div className="gwHint">No audit events loaded.</div>}
            {(events || []).slice().reverse().map((event) => (
              <div className="gwFleetEvent" key={event.event_id}>
                <strong>{fleetRolloutEventLabel(event.type)}</strong>
                <span>{new Date(event.occurred_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SigningPolicyReviewModal({ preview, reason, setReason, busy, onClose, onConfirm }) {
  return (
    <div className="gwOverlay" onClick={onClose} style={{ zIndex: 116 }}>
      <div
        className="gwModal"
        data-gw-ai-form-control="reviewed-plc-deployment"
        data-gw-ai-protected-submit="reviewed-plc-signing-confirm"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Review deployment security</div>
            <div className="gwModalSubtitle">PLC applications will be verified before installation.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwNotice isWarning">
            Deployment security can be set up here only before the first runtime is enrolled.
          </div>
          <div className="gwFleetDigestBox">
            <strong>Security settings</strong>
            <div>Applications remain installable for {signingTTLLabel(preview.artifact_ttl_seconds)} after publishing.</div>
            <div>Control Seat stores and manages the verification credentials.</div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Reason for change</label>
            <textarea
              className="gwTextarea"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why is deployment security being changed?"
            />
            <div className="gwHint">Enter at least 8 characters.</div>
          </div>
          <div className="gwHeaderActions">
            <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Keep current settings</button>
            <button type="button" className="gwPrimaryBtn" onClick={onConfirm} disabled={busy || reason.trim().length < 8}>
              {busy ? "Activating…" : "Activate security"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PlcFleetRolloutsPage({ routeSearch = "" }) {
  const canConfigure = usePermission("configure");
  const canWrite = usePermission("write");
  const canOperate = canConfigure && canWrite;
  const notify = useNotify();
  const [rollouts, setRollouts] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [signingPolicy, setSigningPolicy] = useState(null);
  const [signingLoadError, setSigningLoadError] = useState("");
  const [signingPreview, setSigningPreview] = useState(null);
  const [signingTTL, setSigningTTL] = useState("604800");
  const [signingLabel, setSigningLabel] = useState("Control Seat managed signing");
  const [signingTicket, setSigningTicket] = useState("");
  const [signingReason, setSigningReason] = useState("");
  const [signingBusy, setSigningBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [programID, setProgramID] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [executionMode, setExecutionMode] = useState("compiled");
  const [runPolicy, setRunPolicy] = useState("preserve");
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [canaryID, setCanaryID] = useState("");
  const [fleetConcurrency, setFleetConcurrency] = useState("1");
  const [detail, setDetail] = useState(null);
  const [events, setEvents] = useState([]);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [proposedControl, setProposedControl] = useState(null);
  const detailID = detail?.id || "";
  const signingSetupRequested = deploymentSigningOpenFromSearch(routeSearch);
  // In-flight + generation guard (same pattern as EdgeConnectorsModal /
  // platform-edge-runtimes): a slow poll resolving out of order must not
  // stack requests or overwrite fresher data.
  const loadStateRef = useRef({ inFlight: false, generation: 0 });

  const load = useCallback(async () => {
    if (loadStateRef.current.inFlight) return;
    loadStateRef.current.inFlight = true;
    const generation = ++loadStateRef.current.generation;
    try {
      const [rolloutList, gatewayList, programResponse, signingResult] = await Promise.all([
        listPLCFleetRollouts(), listEdgeGateways(), listPLCProgramsForFleet(),
        getPLCDeploymentSigningPolicy()
          .then((value) => ({ value, error: "" }))
          .catch(() => ({ value: null, error: "Deployment security is unavailable." })),
      ]);
      if (generation !== loadStateRef.current.generation) return;
      setRollouts(Array.isArray(rolloutList) ? rolloutList : []);
      setGateways(Array.isArray(gatewayList) ? gatewayList : []);
      const nextPrograms = Array.isArray(programResponse?.programs) ? programResponse.programs : [];
      setPrograms(nextPrograms);
      setSigningPolicy(signingResult.value);
      setSigningLoadError(signingResult.error);
    } catch (_err) {
      if (generation !== loadStateRef.current.generation) return;
      notify({ type: "error", title: "Applications unavailable", message: "Could not load PLC applications. Try again." });
    } finally {
      loadStateRef.current.inFlight = false;
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const handler = (event) => {
      const payload = event.detail?.payload || {};
      if (payload.program_id) setProgramID(String(payload.program_id));
      if (payload.name) setName(String(payload.name));
      if (payload.reason) setReason(String(payload.reason));
      if (["compiled", "compatibility"].includes(payload.execution_mode)) setExecutionMode(payload.execution_mode);
      if (["preserve", "start", "stop"].includes(payload.run_policy)) setRunPolicy(payload.run_policy);
      if (Array.isArray(payload.gateway_ids)) setSelectedIDs(payload.gateway_ids.map(String));
      if (payload.canary_gateway_id) setCanaryID(String(payload.canary_gateway_id));
    };
    window.addEventListener("gw-ai-propose-plc-fleet-rollout-page", handler);
    return () => window.removeEventListener("gw-ai-propose-plc-fleet-rollout-page", handler);
  }, []);

  useEffect(() => {
    const handler = async (event) => {
      const payload = event.detail?.payload || {};
      if (!payload.rollout_id || !["pause", "cancel", "rollback"].includes(payload.action)) return;
      try {
        const [record, audit] = await Promise.all([
          getPLCFleetRollout(String(payload.rollout_id)),
          listPLCFleetRolloutEvents(String(payload.rollout_id)),
        ]);
        setProposedControl(payload);
        setDetail(record);
        setApprovalRequired(Boolean(record.approval_required));
        setEvents(Array.isArray(audit) ? audit : []);
      } catch (_err) {
        notify({ type: "error", title: "Deployment unavailable", message: "Could not open the requested deployment." });
      }
    };
    window.addEventListener("gw-ai-propose-plc-fleet-control-page", handler);
    return () => window.removeEventListener("gw-ai-propose-plc-fleet-control-page", handler);
  }, [notify]);

  useEffect(() => {
    if (!detailID) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const [record, audit] = await Promise.all([
          getPLCFleetRollout(detailID),
          listPLCFleetRolloutEvents(detailID),
        ]);
        if (!cancelled) {
          setDetail(record);
          setApprovalRequired(Boolean(record.approval_required));
          setEvents(Array.isArray(audit) ? audit : []);
        }
      } catch (_err) {
        // Keep the last authenticated snapshot. Manual refresh reports errors.
      }
    };
    const timer = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [detailID]);

  const publishedPrograms = useMemo(() => programs.filter((program) => Number(program.published_version) > 0), [programs]);
  const applicationRows = useMemo(() => buildPLCApplicationRows(programs, gateways), [programs, gateways]);
  const programOptions = useMemo(() => publishedPrograms.map((program) => ({ value: program.id, label: `${program.name} · version ${program.published_version}` })), [publishedPrograms]);
  const selectedGateways = gateways.filter((gateway) => selectedIDs.includes(gateway.id));
  const onlineGateways = gateways.filter((gateway) => gateway.status === "online");
  const signingAction = signingPolicyAction(signingPolicy, gateways.length);

  const toggleGateway = (gatewayID) => {
    setSelectedIDs((current) => current.includes(gatewayID) ? current.filter((id) => id !== gatewayID) : [...current, gatewayID]);
    if (canaryID === gatewayID && selectedIDs.includes(gatewayID)) setCanaryID("");
  };

  const reviewSigningPolicy = async () => {
    if (signingAction.blocked || !canConfigure) return;
    setSigningBusy(true);
    try {
      const result = await previewPLCDeploymentSigningPolicy({
        artifact_ttl_seconds: Number(signingTTL),
        source_metadata: {
          label: signingLabel.trim(),
          change_ticket: signingTicket.trim(),
        },
      });
      setSigningPreview(result);
      setSigningReason("");
    } catch (_error) {
      notify({ type: "error", title: "Security review failed", message: "Could not prepare the security change. Try again." });
    } finally {
      setSigningBusy(false);
    }
  };

  const confirmSigningPolicy = async () => {
    if (!signingPreview || signingReason.trim().length < 8) return;
    setSigningBusy(true);
    try {
      const status = await confirmPLCDeploymentSigningPolicy({
        preview_token: signingPreview.preview_token,
        preview_digest: signingPreview.preview_digest,
        operator_reason: signingReason.trim(),
      });
      setSigningPolicy(status);
      setSigningPreview(null);
      setSigningReason("");
      notify({
        type: "success",
        title: "Deployment security active",
        message: "New runtimes will verify PLC applications before installation.",
      });
      await load();
    } catch (_error) {
      setSigningPreview(null);
      notify({ type: "error", title: "Security activation failed", message: "The settings were not changed. Review them and try again." });
    } finally {
      setSigningBusy(false);
    }
  };

  const preview = async () => {
    if (!programID || selectedGateways.length === 0 || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const canary = selectedGateways.find((gateway) => gateway.id === canaryID);
      const fleet = selectedGateways.filter((gateway) => gateway.id !== canaryID);
      const stages = [];
      if (canary) stages.push({
        name: "Canary", gateway_ids: [canary.id], concurrency: 1,
        timeout_seconds: 300, failure_threshold: 1, auto_rollback_on_failure: true,
      });
      if (fleet.length > 0) stages.push({
        name: "Fleet", gateway_ids: fleet.map((gateway) => gateway.id),
        concurrency: Math.max(1, Math.min(Number(fleetConcurrency) || 1, fleet.length)),
        timeout_seconds: 600, failure_threshold: 1, auto_rollback_on_failure: true,
      });
      const response = await previewPLCFleetRollout({
        program_id: programID, name: name.trim(), reason: reason.trim(),
        execution_mode: executionMode, run_policy: runPolicy,
        validity_seconds: 3600,
        health: { heartbeat_fresh_seconds: 30, stable_seconds: 30, max_overruns: 0, max_scan_micros: 100000, allow_io_warnings: false },
        stages,
      });
      setDetail(response.rollout);
      setProposedControl(null);
      setApprovalRequired(Boolean(response.approval_required));
      setEvents([]);
      await load();
    } catch (_err) {
      notify({ type: "error", title: "Deployment review failed", message: "Could not prepare this deployment. Check the selected runtimes and try again." });
    } finally {
      setBusy(false);
    }
  };

  const openRollout = async (id) => {
    try {
      const [record, audit] = await Promise.all([getPLCFleetRollout(id), listPLCFleetRolloutEvents(id)]);
      setDetail(record);
      setProposedControl(null);
      setEvents(Array.isArray(audit) ? audit : []);
      setApprovalRequired(Boolean(record.approval_required));
    } catch (_err) {
      notify({ type: "error", title: "Deployment unavailable", message: "Could not load this deployment." });
    }
  };

  const refreshDetail = async () => {
    if (!detailID) return;
    try {
      const [record, audit] = await Promise.all([getPLCFleetRollout(detailID), listPLCFleetRolloutEvents(detailID)]);
      setDetail(record);
      setApprovalRequired(Boolean(record.approval_required));
      setEvents(Array.isArray(audit) ? audit : []);
      await load();
    } catch (_err) {
      notify({ type: "error", title: "Refresh failed", message: "Could not refresh this deployment." });
    }
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Applications</div>
            <div className="gwPageSubtitle">See where each PLC application is running and open it in the editor.</div>
          </div>
          <button type="button" className="gwIconButton" onClick={load} aria-label="Refresh applications" title="Refresh">
            <img src={refreshIcon} alt="" className="gwIcon" />
          </button>
        </div>

        <div className="gwCardSection" data-gw-card="plc_applications">
          <div className="gwSectionTitleRow gwSectionTitle">
            <span>{applicationRows.length} application{applicationRows.length === 1 ? "" : "s"}</span>
          </div>
          <div className="gwCardSectionBody">
            {loading && applicationRows.length === 0 ? <div className="gwHint">Loading PLC applications…</div> : null}
            {!loading && applicationRows.length === 0 ? (
              <div className="gwEmptyState gwEmptyCompact">
                <div className="gwEmptyTitle">No PLC applications yet</div>
                <div className="gwEmptyBody">Create the first PLC project in the editor. It will appear here with its local and edge runtime state.</div>
                <div className="gwEmptyActions"><a className="gwPrimaryBtn" href={PLC_EDITOR_URL}>Open PLC editor</a></div>
              </div>
            ) : null}
            {applicationRows.length > 0 ? (
              <div className="plcApplicationList">
                {applicationRows.map((application) => (
                  <div className="plcApplicationRow" key={application.id} data-gw-resource="plc_programs" data-gw-id={application.id}>
                    <div className="plcApplicationMain">
                      <div className="plcApplicationTitleRow">
                        <strong>{application.name || "Untitled PLC application"}</strong>
                      </div>
                      <div className="gwListMeta">
                        {application.publishedVersion > 0 ? `Version ${application.publishedVersion}` : "Draft only"}
                        {application.draft_dirty ? " · Unpublished changes" : ""}
                      </div>
                      {application.targets.length > 0 ? (
                        <div className="plcApplicationTargets" aria-label={`${application.name} runtime state`}>
                          {application.targets.map((target) => (
                            <span key={target.id} className="plcApplicationTarget">
                              <span className={`plcApplicationStateDot ${target.tone}`} aria-hidden="true" />
                              <span className="plcApplicationTargetName">{target.name}</span>
                              <span className="plcApplicationState">{target.label}</span>
                            </span>
                          ))}
                        </div>
                      ) : <div className="gwHint">Publish this application before deploying it to a runtime.</div>}
                    </div>
                    <a className="gwRowAction" href={PLC_EDITOR_URL}>Open in editor</a>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <details
          className="gwCardSection plcDeploymentAdvanced"
          open={signingSetupRequested || undefined}
          data-gw-card="plc_deployment_signing"
          data-gw-ai-form-control="reviewed-plc-deployment"
        >
          <summary className="gwSectionTitleRow gwSectionTitle plcAdvancedSummary">
            <span className="plcAdvancedSummaryText">
              <span>Deployment security</span>
              <small>Verify PLC applications before they are installed.</small>
            </span>
            <span className="plcAdvancedSummaryState">
              <GatewayStatusPill tone={signingPolicyTone(signingPolicy)}>
                {signingPolicy?.active ? "Active" : "Setup required"}
              </GatewayStatusPill>
              <img src={chevronRightIcon} alt="" className="gwIcon plcAdvancedChevron" />
            </span>
          </summary>
          <div className="gwCardSectionBody gwFormStack">
            {signingLoadError && <div className="gwInlineNotice isWarning">{signingLoadError}</div>}
            {signingPolicy && (
              <>
                <div className="gwFleetSummary">
                  <span>{signingPolicy.mode === "environment" ? "Managed automatically" : "Managed by Control Seat"}</span>
                  {signingPolicy.artifact_ttl_seconds > 0 && <span>Install window: {signingTTLLabel(signingPolicy.artifact_ttl_seconds)}</span>}
                </div>
                {signingPolicy.last_activation?.state === "failed" && (
                  <div className="gwInlineNotice isWarning">The last security change did not complete. Review the settings and try again.</div>
                )}
                {gateways.length > 0 && signingPolicy.mutable && (
                  <div className="gwNotice isWarning">
                    Security changes are unavailable after runtimes are enrolled. Existing runtimes keep their current security settings.
                  </div>
                )}
                {signingPolicy.mutable && gateways.length === 0 && (
                  <div className="gwFieldRow">
                    <div className="gwField" style={{ flex: 1 }}>
                      <label className="gwLabel">Security label</label>
                      <input className="gwInput" value={signingLabel} onChange={(event) => setSigningLabel(event.target.value)} maxLength={120} disabled={!canConfigure || signingBusy} />
                    </div>
                    <div className="gwField" style={{ minWidth: 180 }}>
                      <label className="gwLabel">Install window</label>
                      <Select value={signingTTL} onChange={setSigningTTL} options={[
                        { value: "3600", label: "1 hour" },
                        { value: "86400", label: "1 day" },
                        { value: "604800", label: "7 days" },
                        { value: "2592000", label: "30 days" },
                      ]} disabled={!canConfigure || signingBusy} />
                    </div>
                    <div className="gwField" style={{ flex: 1 }}>
                      <label className="gwLabel">Change reference</label>
                      <input className="gwInput" value={signingTicket} onChange={(event) => setSigningTicket(event.target.value)} maxLength={120} placeholder="Optional" disabled={!canConfigure || signingBusy} />
                    </div>
                  </div>
                )}
                {!signingAction.blocked && (
                  <button type="button" className="gwPrimaryBtn" onClick={reviewSigningPolicy} disabled={!canConfigure || signingBusy}>
                    {signingBusy ? "Preparing review…" : signingAction.label}
                  </button>
                )}
                {signingPolicy.mode === "environment" && <div className="gwHint">Control Seat manages this setting automatically.</div>}
              </>
            )}
          </div>
        </details>

        <details
          className="gwCardSection plcDeploymentAdvanced"
          data-gw-card="plc_fleet_create"
          data-gw-ai-form-control="reviewed-plc-deployment"
        >
          <summary className="gwSectionTitleRow gwSectionTitle plcAdvancedSummary">
            <span className="plcAdvancedSummaryText">
              <span>Deploy to multiple runtimes</span>
              <small>Stage one application across several runtimes with automatic rollback.</small>
            </span>
            <img src={chevronRightIcon} alt="" className="gwIcon plcAdvancedChevron" />
          </summary>
          <div className="gwCardSectionBody gwFleetBuilder">
            <div className="gwField">
              <label className="gwLabel">Published application</label>
              <Select value={programID} onChange={setProgramID} options={programOptions} placeholder="Choose an application" disabled={!canConfigure} />
            </div>
            <div className="gwFieldRow">
              <div className="gwField" style={{ flex: 1 }}>
                <label className="gwLabel">Deployment name</label>
                <input className="gwInput" value={name} onChange={(event) => setName(event.target.value)} placeholder="Packaging line update" disabled={!canConfigure} />
              </div>
              <div className="gwField" style={{ minWidth: 180 }}>
                <label className="gwLabel">Runtime engine</label>
                <Select value={executionMode} onChange={setExecutionMode} options={[
                  { value: "compiled", label: "Compiled (recommended)" },
                  { value: "compatibility", label: "Compatibility" },
                ]} disabled={!canConfigure} />
              </div>
              <div className="gwField" style={{ minWidth: 180 }}>
                <label className="gwLabel">After deployment</label>
                <Select value={runPolicy} onChange={setRunPolicy} options={[
                  { value: "preserve", label: "Preserve each runtime" },
                  { value: "start", label: "Start program" },
                  { value: "stop", label: "Keep program stopped" },
                ]} disabled={!canConfigure} />
              </div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Reason for change</label>
              <textarea className="gwTextarea" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="What changed, why now, and what should operators watch?" disabled={!canConfigure} />
            </div>

            <div className="gwSectionTitleRow gwSectionTitle">
              <span>Target runtimes</span>
              <span className="gwSectionBadge">{selectedGateways.length} selected</span>
            </div>
            <div className="gwFleetRuntimePicker">
              {onlineGateways.length === 0 && <div className="gwHint">No online runtimes are available.</div>}
              {onlineGateways.map((gateway) => (
                <div className="gwFleetRuntimeChoice" key={gateway.id}>
                  <label>
                    <input type="checkbox" checked={selectedIDs.includes(gateway.id)} onChange={() => toggleGateway(gateway.id)} disabled={!canConfigure} />
                    <span><strong>{gateway.name}</strong><small>{gateway.hostname || "Online"}</small></span>
                  </label>
                  {selectedIDs.includes(gateway.id) && (
                    <label className="gwFleetCanary">
                      <input type="radio" name="fleet-canary" checked={canaryID === gateway.id} onChange={() => setCanaryID(gateway.id)} /> Canary
                    </label>
                  )}
                </div>
              ))}
            </div>
            <div className="gwField" style={{ maxWidth: 220 }}>
              <label className="gwLabel">Runtimes updated at once</label>
              <NudgeNumber value={fleetConcurrency} onChange={(value) => setFleetConcurrency(value == null ? "" : String(value))} min={1} max={Math.max(1, onlineGateways.length)} aria-label="Runtimes updated at once" disabled={!canConfigure} />
            </div>
            <div className="gwNotice isSoft">Reviewing prepares the deployment and its rollback. No runtime changes until you approve it.</div>
            {!signingPolicy?.active && <div className="gwInlineNotice isWarning">Deployment security must be active before you can continue.</div>}
            <button type="button" className="gwPrimaryBtn" disabled={!signingPolicy?.active || !canConfigure || busy || !programID || selectedGateways.length === 0 || reason.trim().length < 3} onClick={preview}>
              {busy ? "Preparing review…" : "Review deployment"}
            </button>
          </div>
        </details>

        <div className="gwCardSection" data-gw-card="plc_fleet_rollouts">
          <div className="gwSectionTitleRow gwSectionTitle">
            <span>Deployment history</span>
          </div>
          <div className="gwCardSectionBody">
            {loading && <div className="gwHint">Loading deployment history…</div>}
            {!loading && rollouts.length === 0 && <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyTitle">No deployments yet</div><div className="gwEmptyBody">Deploy to one runtime from the PLC editor, or use the section above for several runtimes.</div></div>}
            <div className="gwFleetList">
              {rollouts.map((item) => (
                <button type="button" className="gwFleetRow" key={item.id} onClick={() => openRollout(item.id)}>
                  <span><strong>{item.name}</strong><small>{item.target_count} runtimes · updated {new Date(item.updated_at).toLocaleString()}</small></span>
                  <span><GatewayStatusPill tone={fleetRolloutPhaseTone(item.phase)}>{fleetRolloutPhaseLabel(item.phase)}</GatewayStatusPill></span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {detail && (
        <RolloutModal
          rollout={detail}
          approvalRequired={approvalRequired}
          proposedControl={proposedControl}
          canOperate={canOperate}
          events={events}
          onClose={() => { setDetail(null); setProposedControl(null); }}
          onRefresh={refreshDetail}
          onConfirmed={(updated) => { setDetail(updated); load(); }}
        />
      )}
      {signingPreview && (
        <SigningPolicyReviewModal
          preview={signingPreview}
          reason={signingReason}
          setReason={setSigningReason}
          busy={signingBusy}
          onClose={() => { if (!signingBusy) { setSigningPreview(null); setSigningReason(""); } }}
          onConfirm={confirmSigningPolicy}
        />
      )}
    </div>
  );
}
